import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, X } from 'lucide-react';

export const UNTAGGED = '__untagged__';

interface Props {
  allTags: string[];
  selected: Set<string>; // tag names; may contain UNTAGGED for the untagged bucket
  onChange: (next: Set<string>) => void;
}

export default function TagFilterDropdown({ allTags, selected, onChange }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const options = [UNTAGGED, ...allTags];
  const allSelected = options.length > 0 && options.every((o) => selected.has(o));

  function toggle(tag: string) {
    const next = new Set(selected);
    if (next.has(tag)) next.delete(tag);
    else next.add(tag);
    onChange(next);
  }
  function selectAll() {
    onChange(new Set(options));
  }
  function clearAll() {
    onChange(new Set());
  }

  const chips = [...selected];

  return (
    <div ref={ref} className="relative">
      <div
        onClick={() => setOpen((o) => !o)}
        className="flex min-h-[40px] cursor-pointer items-center gap-2 rounded-md border border-gray-300 bg-white px-2 py-1.5 hover:border-gray-400"
      >
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-gray-400 transition ${open ? 'rotate-180' : ''}`}
        />
        <div className="flex flex-1 flex-wrap items-center gap-1">
          {chips.length === 0 ? (
            <span className="text-sm text-gray-400">{t('insights.filterByTags')}</span>
          ) : allSelected ? (
            <span className="text-sm text-gray-600">{t('insights.allTagsSelected')}</span>
          ) : (
            chips.map((tag) => (
              <span
                key={tag}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
                  tag === UNTAGGED ? 'bg-gray-100 text-gray-700' : 'bg-blue-50 text-blue-700'
                }`}
              >
                {tag === UNTAGGED ? t('insights.untagged') : tag}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(tag);
                  }}
                  className="text-current opacity-60 hover:opacity-100"
                  aria-label="remove"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))
          )}
        </div>
        {chips.length > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              clearAll();
            }}
            className="shrink-0 text-gray-400 hover:text-gray-600"
            aria-label="clear filter"
            title={t('insights.clearAllTags')}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-auto rounded-md border bg-white shadow-lg">
          <div className="sticky top-0 flex items-center justify-between gap-2 border-b bg-white px-3 py-1.5 text-xs">
            <button
              type="button"
              onClick={selectAll}
              className="text-gray-600 underline-offset-2 hover:text-gray-900 hover:underline"
            >
              {t('insights.selectAllTags')}
            </button>
            <button
              type="button"
              onClick={clearAll}
              className="text-gray-600 underline-offset-2 hover:text-gray-900 hover:underline"
            >
              {t('insights.clearAllTags')}
            </button>
          </div>
          {options.map((tag) => {
            const isSel = selected.has(tag);
            return (
              <label
                key={tag}
                className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-gray-50"
              >
                <span className={tag === UNTAGGED ? 'italic text-gray-500' : 'text-gray-800'}>
                  {tag === UNTAGGED ? t('insights.untagged') : tag}
                </span>
                <input
                  type="checkbox"
                  checked={isSel}
                  onChange={() => toggle(tag)}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
              </label>
            );
          })}
          {allTags.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-400">{t('insights.noTags')}</div>
          )}
        </div>
      )}
    </div>
  );
}
